import { CheckCircleIcon, InfoIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import type { ConnectionStatus } from '../../hooks/use-sse-stream.ts'
import { Routes } from '../../routes.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './freeform-upload-activity.css.ts'
import {
  type FreeformStatusPhase,
  type FreeformUnassociatedItem,
  type FreeformUploadEvent,
  useFreeformUploadEvents,
} from './use-freeform-upload-events.ts'

type RepoScan = {
  repoFullName: string
  scanId: string
  findingCount: number
  lowestConfidence?: number
  weakestRationale?: string
}

// Association is a judgment the backend agent makes, not a string match, so a scan can land on the
// wrong repository. Below this the UI says so: the agent's own confidence is the only signal a user
// has that a scan is worth double-checking before acting on its fixes.
const LOW_CONFIDENCE_THRESHOLD = 0.6

type ReasonCopy = {
  label: string
  description: string
}

// Human copy for each machine-readable outcome. The vocabulary is the union of the analysis-service
// association taxonomy and the platform's scan-creation outcomes; an unrecognized reason falls back
// to a neutral bucket so a new backend value degrades to "logged for review" rather than a blank.
// The router framing matters: an unassociated finding was NOT judged safe — it just could not be
// turned into a scan — so the copy avoids "already fixed"/"not a problem" language.
const UNASSOCIATED_REASON_COPY: Record<string, ReasonCopy> = {
  not_code: {
    label: 'No application code to scan',
    description: 'No source-code location — a runtime header, TLS/proxy/deploy setting, or a third-party console.',
  },
  repo_not_onboarded: {
    label: 'Repository not connected',
    description: "The affected code is in a repository that isn't connected to Pixee.",
  },
  ambiguous: {
    label: 'Repository could not be confirmed',
    description: 'Several connected repositories matched and none could be confirmed as the right one.',
  },
  insufficient_information: {
    label: 'Could not be located',
    description: 'Not enough detail to locate this finding in any connected repository.',
  },
  association_failed: {
    label: 'Processing error',
    description: "Matching didn't finish for this finding; it was logged so nothing is silently dropped.",
  },
  repository_no_longer_exists: {
    label: 'Repository unavailable',
    description: 'Pixee matched a repository, but it no longer exists.',
  },
  default_branch_head_unresolved: {
    label: 'Branch unavailable',
    description: "Pixee matched a repository, but its default branch couldn't be resolved.",
  },
  scan_creation_failed: {
    label: "Scan couldn't be created",
    description: 'Pixee matched a repository but could not create a scan for it.',
  },
}

const FALLBACK_REASON_COPY: ReasonCopy = {
  label: 'Logged for review',
  description: 'Pixee could not turn this finding into a scan.',
}

// Preferred display order — the outcomes a user can act on (connect a repo, check a runtime setting)
// come before the operational ones. Reasons not listed here (including unknown ones) sort after, by
// first appearance, so the ordering is always stable.
const REASON_ORDER = [
  'not_code',
  'repo_not_onboarded',
  'ambiguous',
  'insufficient_information',
  'repository_no_longer_exists',
  'default_branch_head_unresolved',
  'scan_creation_failed',
  'association_failed',
]

const describeReason = (reason: string): ReasonCopy => UNASSOCIATED_REASON_COPY[reason] ?? FALLBACK_REASON_COPY

type UnassociatedGroup = {
  reason: string
  copy: ReasonCopy
  findings: FreeformUnassociatedItem[]
}

// Group the unassociated findings by outcome so the UI shows one section per reason rather than a
// flat list. Order follows REASON_ORDER first, then any remaining reasons by first appearance.
export const groupUnassociatedByReason = (findings: FreeformUnassociatedItem[]): UnassociatedGroup[] => {
  const byReason = new Map<string, FreeformUnassociatedItem[]>()
  for (const finding of findings) {
    const existing = byReason.get(finding.reason)
    if (existing === undefined) {
      byReason.set(finding.reason, [finding])
    } else {
      existing.push(finding)
    }
  }

  const rank = (reason: string): number => {
    const index = REASON_ORDER.indexOf(reason)
    return index === -1 ? REASON_ORDER.length : index
  }

  return [...byReason.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([reason, groupFindings]) => ({ reason, copy: describeReason(reason), findings: groupFindings }))
}

export type FreeformProgress = {
  phase: FreeformStatusPhase
  detail?: string
  files: { filename: string; parseStatus: string }[]
  repoScans: RepoScan[]
  unassociatedCount: number
  unassociatedFindings: FreeformUnassociatedItem[]
  isDone: boolean
  errorReason?: string
}

// Fold the replayed SSE event list (§B8) into a single render-ready summary. The events endpoint
// replays full history on every connection, so deriving from the whole list (rather than mutating on
// each event) stays correct across reconnects.
export const deriveFreeformProgress = (events: FreeformUploadEvent[]): FreeformProgress => {
  let phase: FreeformStatusPhase = 'parsing'
  let detail: string | undefined
  const filesByName = new Map<string, string>()
  const scansById = new Map<string, RepoScan>()
  let unassociatedCount = 0
  let unassociatedFindings: FreeformUnassociatedItem[] = []
  let isDone = false
  let errorReason: string | undefined

  for (const event of events) {
    match(event)
      .with({ type: 'status' }, statusEvent => {
        phase = statusEvent.phase
        detail = statusEvent.detail
      })
      .with({ type: 'file' }, fileEvent => {
        filesByName.set(fileEvent.filename, fileEvent.parseStatus)
      })
      .with({ type: 'repo_scan' }, scanEvent => {
        scansById.set(scanEvent.scanId, {
          repoFullName: scanEvent.repoFullName,
          scanId: scanEvent.scanId,
          findingCount: scanEvent.findingCount,
          lowestConfidence: scanEvent.lowestConfidence,
          weakestRationale: scanEvent.weakestRationale,
        })
      })
      .with({ type: 'unassociated' }, unassociatedEvent => {
        unassociatedCount = unassociatedEvent.count
        unassociatedFindings = unassociatedEvent.findings ?? []
      })
      .with({ type: 'done' }, () => {
        isDone = true
      })
      .with({ type: 'error' }, errorEvent => {
        isDone = true
        errorReason = errorEvent.reason
        phase = 'failed'
      })
      .exhaustive()
  }

  return {
    phase,
    detail,
    files: [...filesByName.entries()].map(([filename, parseStatus]) => ({ filename, parseStatus })),
    repoScans: [...scansById.values()],
    unassociatedCount,
    unassociatedFindings,
    isDone,
    errorReason,
  }
}

const getPhaseLabel = (phase: FreeformStatusPhase): string =>
  match(phase)
    .with('parsing', () => 'Parsing your upload…')
    .with('associating', () => 'Searching your repositories to place each finding…')
    .with('scanning', () => 'Creating scans…')
    .with('completed', () => 'Analysis started')
    .with('failed', () => 'Upload failed')
    .exhaustive()

// Surfaces the weakest association behind a scan. The rationale rides along as the tooltip rather
// than inline text: it is a sentence of prose, and the row is a list item, not a report.
const LowConfidenceChip: React.FC<{ scan: RepoScan }> = ({ scan }) => {
  const { lowestConfidence, weakestRationale } = scan
  const isLowConfidence = lowestConfidence !== undefined && lowestConfidence < LOW_CONFIDENCE_THRESHOLD
  if (!isLowConfidence) {
    return undefined
  }

  const rationale = weakestRationale ?? 'Pixee was not confident this finding belongs to this repository.'

  return (
    <TooltipWithHover
      trigger={
        <span className={styles.lowConfidenceChip} tabIndex={0}>
          <WarningCircleIcon size={12} weight="fill" aria-hidden />
          Low confidence
        </span>
      }
    >
      {rationale}
    </TooltipWithHover>
  )
}

// The agent's per-finding explanation of the outcome. It is a sentence of prose, so it rides in a
// tooltip behind a small "Why?" affordance rather than crowding the finding row inline.
const RationaleChip: React.FC<{ rationale: string }> = ({ rationale }) => (
  <TooltipWithHover
    trigger={
      <span className={styles.rationaleChip} tabIndex={0}>
        <InfoIcon size={12} weight="fill" aria-hidden />
        Why?
      </span>
    }
  >
    {rationale}
  </TooltipWithHover>
)

const UnassociatedBreakdown: React.FC<{ findings: FreeformUnassociatedItem[] }> = ({ findings }) => (
  <>
    {groupUnassociatedByReason(findings).map(group => (
      <div key={group.reason} className={styles.reasonGroup}>
        <div className={styles.reasonHeader}>
          <span className={styles.reasonLabel}>{group.copy.label}</span>
          <span className={styles.rowMeta}>
            {group.findings.length} {pluralize(group.findings.length, 'finding', 'findings')}
          </span>
        </div>
        <p className={styles.reasonDescription}>{group.copy.description}</p>
        <ul className={styles.list}>
          {group.findings.map((finding, index) => (
            <li key={`${group.reason}-${index}`} className={styles.row}>
              <span className={styles.rowName} title={finding.title}>
                {finding.title}
              </span>
              {finding.rationale !== '' ? <RationaleChip rationale={finding.rationale} /> : null}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </>
)

export type FreeformActivityViewProps = {
  progress: FreeformProgress
  connectionStatus: ConnectionStatus
}

export const FreeformActivityView: React.FC<FreeformActivityViewProps> = ({ progress, connectionStatus }) => {
  const { phase, detail, files, repoScans, unassociatedCount, unassociatedFindings, isDone, errorReason } = progress
  const isFailed = phase === 'failed' || errorReason !== undefined
  const isComplete = isDone && !isFailed
  const isStreamError = connectionStatus === 'error'

  return (
    <section className={styles.card} aria-label="Findings intake activity">
      <header className={styles.header}>
        {match({ isFailed, isComplete })
          .with({ isFailed: true }, () => (
            <WarningCircleIcon size={18} weight="fill" className={styles.failedIcon} aria-hidden />
          ))
          .with({ isComplete: true }, () => (
            <CheckCircleIcon size={18} weight="fill" className={styles.readyIcon} aria-hidden />
          ))
          .otherwise(() => (
            <span className={styles.spinner} aria-hidden />
          ))}
        <div>
          <p className={styles.headerTitle}>{getPhaseLabel(phase)}</p>
          {detail !== undefined ? <p className={styles.headerDetail}>{detail}</p> : null}
        </div>
      </header>

      {isFailed ? (
        <div className={styles.section}>
          <p className={styles.errorText}>
            {errorReason ?? 'Something went wrong while processing this upload. Please try again.'}
          </p>
        </div>
      ) : null}

      {files.length > 0 ? (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Files</p>
          <ul className={styles.list}>
            {files.map(file => (
              <li key={file.filename} className={styles.row}>
                <span className={styles.rowName} title={file.filename}>
                  {file.filename}
                </span>
                <span className={styles.rowMeta}>{file.parseStatus}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={styles.section}>
        <p className={styles.sectionTitle}>Scans created</p>
        {repoScans.length > 0 ? (
          <ul className={styles.list}>
            {repoScans.map(scan => (
              <li key={scan.scanId} className={styles.row}>
                <span className={styles.rowName} title={scan.repoFullName}>
                  {scan.repoFullName}
                </span>
                <span className={styles.rowMeta}>
                  {scan.findingCount} {pluralize(scan.findingCount, 'finding', 'findings')}
                </span>
                <LowConfidenceChip scan={scan} />
                <Link className={styles.scanLink} to={Routes.ScanDetailsPage.createPath({ scanId: scan.scanId })}>
                  View scan
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.emptyNote}>No scans created yet.</p>
        )}
      </div>

      {unassociatedCount > 0 ? (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Not turned into scans</p>
          <p className={styles.unassociatedText}>
            <WarningCircleIcon size={14} weight="fill" aria-hidden />
            {unassociatedCount} {pluralize(unassociatedCount, 'finding', 'findings')}{' '}
            {pluralize(unassociatedCount, 'was', 'were')} logged for review.
          </p>
          {unassociatedFindings.length > 0 ? <UnassociatedBreakdown findings={unassociatedFindings} /> : null}
        </div>
      ) : null}

      {isStreamError && !isDone ? (
        <div className={styles.section}>
          <p className={styles.emptyNote}>
            Lost connection to the progress stream. Processing continues in the background.
          </p>
        </div>
      ) : null}
    </section>
  )
}

type FreeformUploadActivityProps = {
  organizationId?: string
  sessionId: string
}

export const FreeformUploadActivity: React.FC<FreeformUploadActivityProps> = ({ organizationId, sessionId }) => {
  const { events, connectionStatus } = useFreeformUploadEvents({ organizationId, sessionId })
  const progress = deriveFreeformProgress(events)
  return <FreeformActivityView progress={progress} connectionStatus={connectionStatus} />
}
